'use client'

import { ProgressConfetti } from '@/components/ProgressConfetti'
import { contactFormApply } from '@/lib/services/contactFormApplyService'
import { ContactFormTemplate_1_0, ContactFormTemplateProps, translateStaticText } from '@dvinci/attract-studio-components'
import { useState } from 'react'

export const DynamicContactFormTemplateWrapper = (
  props: Omit<ContactFormTemplateProps, 'onSubmit' | 'fieldValues' | 'fieldErrors' | 'onFieldChange' | 'isSubmitted'> & { tenant: string }
) => {
  const [firstNameValue, setFirstNameValue] = useState<string | undefined>()
  const [lastNameValue, setLastNameValue] = useState<string | undefined>()
  const [emailValue, setEmailValue] = useState<string | undefined>()
  const [firstNameError, setFirstNameError] = useState<string | true | undefined>()
  const [lastNameError, setLastNameError] = useState<string | true | undefined>()
  const [emailError, setEmailError] = useState<string | true | undefined>()
  const [showConfetti, setShowConfetti] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [hasFormError, setHasFormError] = useState(false)

  const { language, data, tenant } = props
  const { jobOpeningId } = data

  const REQUIRED_ERROR = translateStaticText('fieldRequiredError', language)
  const INVALID_EMAIL_ERROR = translateStaticText('invalidEmailError', language)
  const FORM_ERROR = translateStaticText('contactFormSubmitError', language)
  const VALID_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  const isBlank = (value?: string) => !value || value.trim().length === 0

  const validate = () => {
    let isValid = true

    if (isBlank(firstNameValue)) {
      setFirstNameError(REQUIRED_ERROR)
      isValid = false
    }

    if (isBlank(lastNameValue)) {
      setLastNameError(REQUIRED_ERROR)
      isValid = false
    }

    if (isBlank(emailValue)) {
      setEmailError(REQUIRED_ERROR)
      isValid = false
    }

    if (emailValue && !VALID_EMAIL_REGEX.test(emailValue)) {
      setEmailError(INVALID_EMAIL_ERROR)
      isValid = false
    }

    return isValid
  }

  return (
    <>
      <ProgressConfetti show={showConfetti} />
      <ContactFormTemplate_1_0
        {...props}
        fieldValues={{
          firstName: firstNameValue,
          lastName: lastNameValue,
          email: emailValue
        }}
        onFieldChange={{
          firstName: (value?: string) => {
            setFirstNameValue(value)
            setFirstNameError(undefined)
          },
          lastName: (value?: string) => {
            setLastNameValue(value)
            setLastNameError(undefined)
          },
          email: (value?: string) => {
            setEmailValue(value)
            setEmailError(undefined)
          }
        }}
        fieldErrors={{
          firstName: firstNameError,
          lastName: lastNameError,
          email: emailError
        }}
        onSubmit={async () => {
          if (validate()) {
            setHasFormError(false)
            const response = await contactFormApply(tenant, Number(jobOpeningId), firstNameValue!, lastNameValue!, emailValue!, language)
            if (response.ok) {
              setShowConfetti(true)
              setIsSubmitted(true)
            } else {
              setHasFormError(true)
            }
          }
        }}
        isSubmitted={isSubmitted}
        errorMessage={hasFormError ? FORM_ERROR : undefined}
      />
    </>
  )
}
